/**
 * 响应式设计工具集
 */

// 断点定义
export const breakpoints = {
  mobile: 767,
  tablet: 1023,
  desktop: 1024,
} as const;

// 媒体查询字符串
export const mediaQueries = {
  mobile: `(max-width: ${breakpoints.mobile}px)`,
  tablet: `(min-width: ${breakpoints.mobile + 1}px) and (max-width: ${breakpoints.tablet}px)`,
  desktop: `(min-width: ${breakpoints.desktop}px)`,
  mobileAndTablet: `(max-width: ${breakpoints.tablet}px)`,
  tabletAndDesktop: `(min-width: ${breakpoints.mobile + 1}px)`,
} as const;

// 响应式样式对象
export const responsive = {
  // 容器宽度
  container: {
    mobile: '100%',
    tablet: '90vw',
    desktop: '75rem', // 1200px，保持桌面端现有布局
  },
  
  // 工具页面容器
  toolContainer: {
    mobile: '100%',
    tablet: '80vw',
    desktop: '43.75rem', // 700px
    large: '56.25rem',   // 900px
  },
  
  // 间距系统
  spacing: {
    xs: '0.25rem',  // 4px
    sm: '0.5rem',   // 8px
    md: '1rem',     // 16px
    lg: '1.5rem',   // 24px
    xl: '2rem',     // 32px
    '2xl': '2.5rem', // 40px
    '3xl': '3rem',   // 48px
  },
  
  // 按钮尺寸
  button: {
    small: {
      mobile: '3rem',    // 48px
      desktop: '3.75rem', // 60px
    },
    medium: {
      mobile: '4rem',    // 64px
      desktop: '5rem',   // 80px
    },
    large: {
      mobile: '5rem',    // 80px
      desktop: '7.5rem', // 120px
    },
  },
  
  // 字体大小
  fontSize: {
    xs: 'clamp(0.75rem, 2vw, 0.875rem)',    // 12-14px
    sm: 'clamp(0.875rem, 2.5vw, 1rem)',     // 14-16px
    md: 'clamp(1rem, 3vw, 1.125rem)',       // 16-18px
    lg: 'clamp(1.125rem, 4vw, 1.5rem)',     // 18-24px
    xl: 'clamp(1.25rem, 5vw, 1.75rem)',     // 20-28px
  },
} as const;

// 获取响应式样式的工具函数
export const getResponsiveStyle = (
  mobileStyle: React.CSSProperties,
  tabletStyle?: React.CSSProperties,
  desktopStyle?: React.CSSProperties
): React.CSSProperties => {
  // 基础移动端样式
  let style = { ...mobileStyle };
  
  // 如果有平板样式，在平板断点以上应用
  if (tabletStyle && window.innerWidth > breakpoints.mobile) {
    style = { ...style, ...tabletStyle };
  }
  
  // 如果有桌面样式，在桌面断点以上应用
  if (desktopStyle && window.innerWidth >= breakpoints.desktop) {
    style = { ...style, ...desktopStyle };
  }
  
  return style;
};

// Hook: 检测当前屏幕类型
export const useScreenType = () => {
  const [screenType, setScreenType] = React.useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  
  React.useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= breakpoints.mobile) {
        setScreenType('mobile');
      } else if (width <= breakpoints.tablet) {
        setScreenType('tablet');
      } else {
        setScreenType('desktop');
      }
    };
    
    // 初始检测
    handleResize();
    
    // 监听窗口大小变化
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return screenType;
};

// Hook: 检测是否为移动端
export const useIsMobile = () => {
  const screenType = useScreenType();
  return screenType === 'mobile';
};

// Hook: 检测是否为桌面端
export const useIsDesktop = () => {
  const screenType = useScreenType();
  return screenType === 'desktop';
};

// 常用的响应式样式组合
export const commonStyles = {
  // 居中容器
  centerContainer: {
    maxWidth: 'var(--container-lg)',
    margin: '0 auto',
    padding: '0 var(--spacing-md)',
  },
  
  // 工具页面容器
  toolPageContainer: {
    maxWidth: 'var(--tool-container-lg)',
    margin: '0 auto',
    padding: 'var(--spacing-md)',
  },
  
  // 响应式卡片
  responsiveCard: {
    margin: 'var(--spacing-sm) 0',
    borderRadius: 'var(--radius-lg)',
  },
  
  // 响应式按钮
  responsiveButton: {
    minWidth: 'var(--btn-md-width)',
    fontSize: 'var(--font-sm)',
    borderRadius: 'var(--radius-md)',
  },
} as const;

// CSS-in-JS 媒体查询帮助函数
export const mq = {
  mobile: `@media ${mediaQueries.mobile}`,
  tablet: `@media ${mediaQueries.tablet}`,
  desktop: `@media ${mediaQueries.desktop}`,
  mobileAndTablet: `@media ${mediaQueries.mobileAndTablet}`,
  tabletAndDesktop: `@media ${mediaQueries.tabletAndDesktop}`,
} as const;

// 导出 React 引用（为了 Hook 使用）
import React from 'react'; 